import { useEffect, useState } from "react";
import { listUsage, summarize, type UsageEvent } from "../lib/usage";
import { PLANS, currentPlan, setPlan, planOf, type PlanId } from "../lib/plan";
import { money } from "../lib/format";

const RANGES: [string, number | undefined][] = [["7 days", 7], ["30 days", 30], ["All time", undefined]];

export function Admin() {
  const [events, setEvents] = useState<UsageEvent[]>([]);
  const [days, setDays] = useState<number | undefined>(30);
  const [plan, setPlanState] = useState<PlanId>(currentPlan());
  const [loading, setLoading] = useState(true);

  const load = () => { setLoading(true); listUsage().then(setEvents).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(load, []);

  const s = summarize(events, days);

  return (
    <div className="wrap">
      <div className="pe">
        <div className="l">◆ Admin — usage, cost &amp; billing (owner only)</div>
        <p>
          Every billable provider call — <b>Claude AI, ATTOM, RentCast, Regrid, Resend, GoHighLevel</b> — is metered here,
          per user, so you always know what's being consumed and what it costs. Figures are <b>estimates</b> for visibility;
          reconcile against provider invoices for exact billing.
        </p>
      </div>

      {/* USAGE */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8, marginBottom: 10 }}>
        <h2 className="sechead" style={{ margin: 0, fontSize: 19 }}>Provider usage</h2>
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          {RANGES.map(([label, d]) => (
            <button key={label} className={"btn btn-sm " + (days === d ? "btn-p" : "btn-g")} onClick={() => setDays(d)}>{label}</button>
          ))}
          <button className="btn btn-g btn-sm" onClick={load}>↻</button>
        </div>
      </div>

      <div className="grid g3" style={{ marginBottom: 12 }}>
        <div className="card kpi"><div className="kl">Est. spend</div><div className="kv">{money(s.total)}</div><div className="ks">{RANGES.find((r) => r[1] === days)?.[0].toLowerCase()}</div></div>
        <div className="card kpi"><div className="kl">Billable calls</div><div className="kv">{s.count}</div></div>
        <div className="card kpi"><div className="kl">Users</div><div className="kv">{s.byActor.length}</div></div>
      </div>

      <div className="grid g2">
        <div className="card pad">
          <div className="eyebrow">By provider</div>
          {s.byProvider.length === 0 ? <div className="note" style={{ marginTop: 8 }}>{loading ? "Loading…" : "No usage recorded in this window yet."}</div> : (
            <div className="tablewrap" style={{ marginTop: 8 }}>
              <table className="t">
                <thead><tr><th>Provider</th><th className="n">Calls</th><th className="n">Est. cost</th></tr></thead>
                <tbody>
                  {s.byProvider.map((p) => (
                    <tr key={p.provider}><td>{p.provider}</td><td className="n">{p.count}</td><td className="n">{p.cost > 0 ? money(p.cost) : "free"}</td></tr>
                  ))}
                  <tr className="tot"><td>Total</td><td className="n">{s.count}</td><td className="n">{money(s.total)}</td></tr>
                </tbody>
              </table>
            </div>
          )}
        </div>
        <div className="card pad">
          <div className="eyebrow">By user</div>
          {s.byActor.length === 0 ? <div className="note" style={{ marginTop: 8 }}>No users yet.</div> : (
            <div className="tablewrap" style={{ marginTop: 8 }}>
              <table className="t">
                <thead><tr><th>User</th><th className="n">Calls</th><th className="n">Est. cost</th></tr></thead>
                <tbody>
                  {s.byActor.map((a) => (
                    <tr key={a.actor}><td className="mono" style={{ fontSize: 11.5 }}>{a.actor}</td><td className="n">{a.count}</td><td className="n">{money(a.cost)}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* RECENT */}
      <h2 className="sechead" style={{ fontSize: 19 }}>Recent activity</h2>
      <div className="card pad">
        {events.length === 0 ? <div className="note">Nothing yet — run an AI analysis, auto-fill a deal, or send a letter and it appears here.</div> : (
          <div className="tablewrap">
            <table className="t">
              <thead><tr><th>When</th><th>User</th><th>Provider</th><th>Action</th><th className="n">Cost</th></tr></thead>
              <tbody>
                {events.slice(0, 40).map((e) => (
                  <tr key={e.id}>
                    <td style={{ fontSize: 11.5, color: "var(--muted)" }}>{new Date(e.created_at).toLocaleString()}</td>
                    <td className="mono" style={{ fontSize: 11 }}>{e.actor}</td>
                    <td>{e.provider}</td>
                    <td style={{ color: "var(--muted)" }}>{e.action}</td>
                    <td className="n">{e.cost > 0 ? money(e.cost) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="note" style={{ marginTop: 8 }}>Government feeds (Census, HUD, FEMA) are free; paid feeds (ATTOM, RentCast, Regrid) and AI carry the estimated per-call cost shown.</div>
      </div>

      {/* PLAN */}
      <h2 className="sechead" style={{ fontSize: 19 }}>Billing plan</h2>
      <div className="card pad">
        <div className="eyebrow">Active plan — controls feature gating</div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 10 }}>
          {PLANS.map((p) => (
            <button key={p.id} className={"chip-toggle" + (plan === p.id ? " on" : "")} onClick={() => { setPlan(p.id); setPlanState(p.id); window.dispatchEvent(new CustomEvent("beacon-plan")); }}>
              {plan === p.id ? "✓ " : ""}{p.name} {p.price ? `· $${p.price}` : "· free"}
            </button>
          ))}
        </div>
        <div className="note" style={{ marginTop: 8 }}>
          You're on <b>{planOf(plan).name}</b>. Switch to preview exactly what each customer tier sees — locked features show an upgrade prompt. (Real billing via Stripe is the next step; today the plan is set here.)
        </div>
      </div>
    </div>
  );
}
